import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';

/**
 * Centralized hook to determine if authentication context is ready for queries
 * This prevents RLS policy errors by ensuring queries only run when:
 * 1. Authentication loading is complete
 * 2. Session is fully established (has JWT token)
 * 3. For authenticated users, ensures both user and session are present
 */
export const useAuthReadiness = () => {
  const { user, session, loading, sessionReady } = useOptimizedAuth();

  // Authentication is ready when:
  // 1. Loading is complete (covers both auth loading and initial session check)
  // 2. If there's a user, we also have a ready session with JWT token
  const isAuthReady = !loading && (!user || (user && sessionReady));
  
  // User is authenticated and ready for user-specific data operations
  const isUserReady = !loading && !!user && sessionReady;
  
  // Safe to make public Supabase queries - always allow after initial loading
  const canQueryPublicData = !loading;
  
  // Safe to make user-specific Supabase queries - requires full auth setup
  const canQueryUserData = isUserReady;

  // Enhanced session validation
  const hasValidSession = session?.access_token && session?.user && sessionReady;
  
  // Debug logs removed for production performance

  return {
    user,
    session,
    loading,
    sessionReady,
    isAuthReady,
    isUserReady,
    canQueryPublicData,
    canQueryUserData,
    hasValidSession,
  };
};